import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  useToast,
  Spinner,
  Text,
  Avatar,
  HStack,
  VStack,
  Container,
  useBreakpointValue,
  Divider,
  Wrap,
  WrapItem,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Flex,
  IconButton,
  FormControl,
  FormLabel,
  Textarea,
  Grid,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { ChatState } from "../Context/ChatProvider";
import axios from "axios";
import EditUserModal from "./EditUserModal";
import CreateUserModal from "./CreateUserModal";
import { categoryIconSuggestions } from "../utils/iconMapper";
import { DynamicIcon } from "../utils/iconMapper";

// Mobile Card Component for Vendors
const VendorCard = ({
  vendor,
  onEdit,
  onApprove,
  onReject,
  showStatus = false,
}) => {
  return (
    <Box
      bg="white"
      p={4}
      borderRadius="md"
      borderWidth="1px"
      shadow="sm"
      mb={3}
    >
      <HStack spacing={3} mb={3}>
        <Avatar size="md" name={vendor.name} src={vendor.pic} />
        <VStack align="start" spacing={0} flex={1}>
          <Text fontWeight="bold" fontSize="md">
            {vendor.name}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {vendor.companyName}
          </Text>
        </VStack>
        {showStatus && (
          <Box>
            {vendor.isApproved === "approved" && (
              <Badge colorScheme="green">Approved</Badge>
            )}
            {vendor.isApproved === "pending" && (
              <Badge colorScheme="orange">Pending</Badge>
            )}
            {vendor.isApproved === "rejected" && (
              <Badge colorScheme="red">Rejected</Badge>
            )}
            {!vendor.isEmailVerified && (
              <Badge colorScheme="red" ml={2}>
                Unverified Email
              </Badge>
            )}
          </Box>
        )}
      </HStack>

      <VStack align="start" spacing={1} mb={3} fontSize="sm">
        <Text>
          <strong>Email:</strong> {vendor.email}
        </Text>
        <Text>
          <strong>Phone:</strong> {vendor.phone}
        </Text>
        <Text>
          <strong>Location:</strong> {vendor.location}
        </Text>
        <Text>
          <strong>Categories:</strong>{" "}
          {vendor.categories?.slice(0, 3).join(", ")}
          {vendor.categories?.length > 3 && "..."}
        </Text>
      </VStack>

      <Divider mb={3} />

      <Wrap spacing={2}>
        <WrapItem>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => onEdit(vendor)}
            width={{ base: "full", sm: "auto" }}
          >
            Edit
          </Button>
        </WrapItem>
        {onApprove && vendor.isApproved !== "approved" && (
          <WrapItem>
            <Button
              size="sm"
              colorScheme="green"
              variant={vendor.isApproved === "pending" ? "solid" : "outline"}
              onClick={() => onApprove(vendor._id)}
              width={{ base: "full", sm: "auto" }}
            >
              Approve
            </Button>
          </WrapItem>
        )}
        {onReject &&
          (vendor.isApproved === "pending" ||
            vendor.isApproved === "approved") && (
            <WrapItem>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={() => onReject(vendor._id)}
                width={{ base: "full", sm: "auto" }}
              >
                Reject
              </Button>
            </WrapItem>
          )}
      </Wrap>
    </Box>
  );
};

// Mobile Card Component for Customers
const CustomerCard = ({ customer, onEdit, onDelete, onBan, onUnban }) => {
  return (
    <Box
      bg="white"
      p={4}
      borderRadius="md"
      borderWidth="1px"
      shadow="sm"
      mb={3}
    >
      <HStack spacing={3} mb={3}>
        <Avatar size="md" name={customer.name} src={customer.pic} />
        <VStack align="start" spacing={0} flex={1}>
          <Text fontWeight="bold" fontSize="md">
            {customer.name}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {customer.email}
          </Text>
        </VStack>
        <VStack spacing={1}>
          <Badge colorScheme={customer.isBanned ? "red" : "green"}>
            {customer.isBanned ? "Banned" : "Active"}
          </Badge>
          {!customer.isEmailVerified && (
            <Badge colorScheme="red">Unverified Email</Badge>
          )}
        </VStack>
      </HStack>

      <Divider mb={3} />

      <Wrap spacing={2}>
        <WrapItem>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => onEdit(customer)}
            width={{ base: "full", sm: "auto" }}
          >
            Edit
          </Button>
        </WrapItem>
        {customer.isBanned
          ? onUnban && (
              <WrapItem>
                <Button
                  size="sm"
                  colorScheme="green"
                  variant="outline"
                  onClick={() => onUnban(customer._id)}
                  width={{ base: "full", sm: "auto" }}
                >
                  Unban
                </Button>
              </WrapItem>
            )
          : onBan && (
              <WrapItem>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => onBan(customer._id)}
                  width={{ base: "full", sm: "auto" }}
                >
                  Ban
                </Button>
              </WrapItem>
            )}
        {onDelete && (
          <WrapItem>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={() => onDelete(customer._id)}
              width={{ base: "full", sm: "auto" }}
            >
              Delete
            </Button>
          </WrapItem>
        )}
      </Wrap>
    </Box>
  );
};

const AdminDashboard = () => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { user, loading } = ChatState();
  const navigate = useNavigate();
  const toast = useToast();

  const [pendingVendors, setPendingVendors] = useState([]);
  const [approvedVendors, setApprovedVendors] = useState([]);
  const [rejectedVendors, setRejectedVendors] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [lowRatedVendors, setLowRatedVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Category management states
  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose,
  } = useDisclosure();
  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    icon: "",
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);

  // Pagination states
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState({
    allVendors: 1,
    pending: 1,
    approved: 1,
    rejected: 1,
    customers: 1,
    lowRated: 1,
  });

  const [jobStats, setJobStats] = useState({
    totalJobs: 0,
    todoJobs: 0,
    doingJobs: 0,
    doneJobs: 0,
    totalEarnings: 0,
    averageEarnings: 0,
  });

  // Filter function for search
  const filterUsers = (users) => {
    if (!searchQuery) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.companyName?.toLowerCase().includes(query) ||
        user.phone?.includes(query) ||
        user.location?.toLowerCase().includes(query)
    );
  };

  // Pagination utility functions
  const getPaginatedData = (data, page) => {
    if (!data || !Array.isArray(data)) return [];
    if (pageSize === Infinity) return data;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (dataLength) => {
    if (!dataLength || pageSize === Infinity) return 1;
    return Math.ceil(dataLength / pageSize) || 1;
  };

  const handlePageChange = (tabName, newPage) => {
    setCurrentPage((prev) => ({ ...prev, [tabName]: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    // Reset all pages to 1 when page size changes
    setCurrentPage({
      allVendors: 1,
      pending: 1,
      approved: 1,
      rejected: 1,
      customers: 1,
      lowRated: 1,
    });
  };

  // Protect admin route
  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    if (user.role !== "admin") {
      toast({
        title: "Access Denied",
        description: "You do not have permission to access this page.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigate("/");
      return;
    }

    fetchDashboardData();
  }, [user, loading, navigate]);

  const fetchDashboardData = async () => {
    try {
      setDataLoading(true);
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Fetch all users and categories
      const [
        usersResponse,
        categoriesResponse,
        jobStatsResponse,
        lowRatedResponse,
      ] = await Promise.all([
        axios.get("/api/user", config),
        axios.get("/api/category"),
        axios.get("/api/jobs/admin/stats", config),
        axios.get("/api/user/low-rated", config),
      ]);

      const userData = usersResponse.data;

      // Separate users by role and approval status
      const pending = userData.filter(
        (u) => u.role === "vendor" && u.isApproved === "pending"
      );
      const approved = userData.filter(
        (u) => u.role === "vendor" && u.isApproved === "approved"
      );
      const rejected = userData.filter(
        (u) => u.role === "vendor" && u.isApproved === "rejected"
      );
      const vendors = userData.filter((u) => u.role === "vendor");
      const customers = userData.filter((u) => u.role === "customer");

      setPendingVendors(pending);
      setApprovedVendors(approved);
      setRejectedVendors(rejected);
      setAllVendors(vendors);
      setAllCustomers(customers);
      setLowRatedVendors(lowRatedResponse.data);
      setCategories(categoriesResponse.data);
      setJobStats(jobStatsResponse.data);
      setDataLoading(false);
    } catch (error) {
      toast({
        title: "Error Loading Data",
        description:
          error.response?.data?.message || "Failed to load dashboard data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setDataLoading(false);
    }
  };

  const handleApproveVendor = async (vendorId) => {
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.put(`/api/user/approve/${vendorId}`, {}, config);

      toast({
        title: "Vendor Approved",
        description: "The vendor has been successfully approved.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh data
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error Approving Vendor",
        description:
          error.response?.data?.message || "Failed to approve vendor",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleRejectVendor = async (vendorId) => {
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.put(`/api/user/reject/${vendorId}`, {}, config);

      toast({
        title: "Vendor Rejected",
        description: "The vendor application has been rejected.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      // Refresh data
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error Rejecting Vendor",
        description: error.response?.data?.message || "Failed to reject vendor",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEditUser = (userData) => {
    setEditUser(userData);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    fetchDashboardData();
  };

  const handleBanCustomer = async (customerId) => {
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.put(`/api/user/ban/${customerId}`, {}, config);

      toast({
        title: "Customer Banned",
        description: "The customer has been banned from the platform.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });

      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error Banning Customer",
        description: error.response?.data?.message || "Failed to ban customer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUnbanCustomer = async (customerId) => {
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.put(`/api/user/unban/${customerId}`, {}, config);

      toast({
        title: "Customer Unbanned",
        description: "The customer has been unbanned.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error Unbanning Customer",
        description:
          error.response?.data?.message || "Failed to unban customer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Category Management Functions
  const handleOpenCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setNewCategory({
        name: category.name,
        description: category.description || "",
        icon: category.icon || "",
      });
    } else {
      setEditingCategory(null);
      setNewCategory({ name: "", description: "", icon: "" });
    }
    onCategoryModalOpen();
  };

  const handleSaveCategory = async () => {
    if (!newCategory.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Category name is required",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setCategoryLoading(true);
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      if (editingCategory) {
        // Update existing category
        await axios.put(
          `/api/category/${editingCategory._id}`,
          newCategory,
          config
        );
        toast({
          title: "Category Updated",
          description: "The category has been successfully updated.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new category
        await axios.post("/api/category", newCategory, config);
        toast({
          title: "Category Created",
          description: "The new category has been successfully created.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      onCategoryModalClose();
      setNewCategory({ name: "", description: "", icon: "" });
      setEditingCategory(null);
      fetchDashboardData();
    } catch (error) {
      toast({
        title: editingCategory
          ? "Error Updating Category"
          : "Error Creating Category",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.delete(`/api/category/${categoryId}`, config);

      toast({
        title: "Category Deleted",
        description: "The category has been successfully deleted.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Error Deleting Category",
        description:
          error.response?.data?.message || "Failed to delete category",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Pagination Controls Component
  const PaginationControls = ({ data, tabName }) => {
    const dataLength = Array.isArray(data) ? data.length : 0;
    const totalPages = getTotalPages(dataLength);
    const currentTabPage = currentPage[tabName];

    return (
      <Flex
        mt={4}
        justify="space-between"
        align="center"
        flexWrap="wrap"
        gap={4}
      >
        <HStack spacing={2}>
          <Text fontSize="sm" color="gray.600">
            Rows per page:
          </Text>
          <Select
            size="sm"
            width="100px"
            value={pageSize === Infinity ? "all" : pageSize}
            onChange={(e) => {
              const value = e.target.value;
              handlePageSizeChange(value === "all" ? Infinity : Number(value));
            }}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="all">All</option>
          </Select>
        </HStack>

        {pageSize !== Infinity && dataLength > 0 && (
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              Page {currentTabPage} of {totalPages}
            </Text>
            <IconButton
              icon={<ChevronLeftIcon />}
              size="sm"
              isDisabled={currentTabPage === 1}
              onClick={() => handlePageChange(tabName, currentTabPage - 1)}
              aria-label="Previous page"
            />
            <IconButton
              icon={<ChevronRightIcon />}
              size="sm"
              isDisabled={currentTabPage >= totalPages}
              onClick={() => handlePageChange(tabName, currentTabPage + 1)}
              aria-label="Next page"
            />
          </HStack>
        )}
      </Flex>
    );
  };

  if (loading || dataLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Container
      maxW={{ base: "100%", md: "container.xl" }}
      px={{ base: 2, md: 4 }}
      py={{ base: 4, md: 8 }}
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        <Heading size={{ base: "lg", md: "xl" }} color="blue.600">
          Admin Dashboard
        </Heading>

        <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <HStack
            spacing={{ base: 2, sm: 4, md: 8 }}
            mb={{ base: 4, md: 6 }}
            flexWrap="wrap"
            justifyContent={{ base: "space-around", md: "flex-start" }}
          >
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="orange.500"
              >
                {pendingVendors.length}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                Pending
              </Text>
            </Box>
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="green.500"
              >
                {approvedVendors.length}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                Approved
              </Text>
            </Box>
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="red.500"
              >
                {rejectedVendors.length}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                Rejected
              </Text>
            </Box>
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="purple.500"
              >
                {allVendors.length}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                Total
              </Text>
            </Box>
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="blue.500"
              >
                {allCustomers.length}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                Customers
              </Text>
            </Box>
          </HStack>
        </Box>

        {/* Job Statistics Section */}
        <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <Heading size={{ base: "sm", md: "md" }} mb={4} color="gray.700">
            Job Statistics
          </Heading>
          <HStack
            spacing={{ base: 2, sm: 4, md: 8 }}
            justify="space-around"
            flexWrap="wrap"
          >
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="purple.500"
              >
                {jobStats.totalJobs}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                Total Jobs
              </Text>
            </Box>
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="blue.500"
              >
                {jobStats.todoJobs}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                To-Do
              </Text>
            </Box>
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="orange.500"
              >
                {jobStats.doingJobs}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                In Progress
              </Text>
            </Box>
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="green.500"
              >
                {jobStats.doneJobs}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                Completed
              </Text>
            </Box>
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="green.600"
              >
                ${jobStats.totalEarnings.toLocaleString()}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                Total Earnings
              </Text>
            </Box>
            <Box
              textAlign="center"
              minW={{ base: "70px", sm: "90px", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "xl", md: "3xl" }}
                fontWeight="bold"
                color="blue.600"
              >
                $
                {jobStats.averageEarnings.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                Avg per Job
              </Text>
            </Box>
          </HStack>
        </Box>

        <HStack spacing={4}>
          <InputGroup size={{ base: "md", md: "lg" }} flex={1}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by name, email, company, phone, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="white"
              borderRadius="lg"
              _focus={{
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px #3182ce",
              }}
            />
          </InputGroup>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="green"
            onClick={() => setIsCreateModalOpen(true)}
            size={{ base: "md", md: "lg" }}
          >
            Create User
          </Button>
        </HStack>

        <Tabs colorScheme="blue" variant="enclosed">
          <TabList
            overflowX="auto"
            overflowY="hidden"
            flexWrap={{ base: "nowrap", md: "wrap" }}
          >
            <Tab fontSize={{ base: "xs", md: "md" }} whiteSpace="nowrap">
              All
            </Tab>
            <Tab fontSize={{ base: "xs", md: "md" }} whiteSpace="nowrap">
              <Text display={{ base: "none", sm: "inline" }}>Pending</Text>
              <Text display={{ base: "inline", sm: "none" }}>Pend.</Text>
              {pendingVendors.length > 0 && (
                <Badge ml={2} colorScheme="orange">
                  {pendingVendors.length}
                </Badge>
              )}
            </Tab>
            <Tab fontSize={{ base: "xs", md: "md" }} whiteSpace="nowrap">
              <Text display={{ base: "none", sm: "inline" }}>Approved</Text>
              <Text display={{ base: "inline", sm: "none" }}>Appr.</Text>
            </Tab>
            <Tab fontSize={{ base: "xs", md: "md" }} whiteSpace="nowrap">
              <Text display={{ base: "none", sm: "inline" }}>Rejected</Text>
              <Text display={{ base: "inline", sm: "none" }}>Rej.</Text>
              {rejectedVendors.length > 0 && (
                <Badge ml={2} colorScheme="red">
                  {rejectedVendors.length}
                </Badge>
              )}
            </Tab>
            <Tab fontSize={{ base: "xs", md: "md" }} whiteSpace="nowrap">
              <Text display={{ base: "none", sm: "inline" }}>Customers</Text>
              <Text display={{ base: "inline", sm: "none" }}>Cust.</Text>
            </Tab>
            <Tab fontSize={{ base: "xs", md: "md" }} whiteSpace="nowrap">
              <Text display={{ base: "none", sm: "inline" }}>Low Rated</Text>
              <Text display={{ base: "inline", sm: "none" }}>⭐≤2</Text>
              {lowRatedVendors.length > 0 && (
                <Badge ml={2} colorScheme="red">
                  {lowRatedVendors.length}
                </Badge>
              )}
            </Tab>
            <Tab fontSize={{ base: "xs", md: "md" }} whiteSpace="nowrap">
              <Text display={{ base: "none", sm: "inline" }}>Categories</Text>
              <Text display={{ base: "inline", sm: "none" }}>Cat.</Text>
              <Badge ml={2} colorScheme="purple">
                {categories.length}
              </Badge>
            </Tab>
          </TabList>

          <TabPanels>
            {/* All Vendors Tab */}
            <TabPanel p={{ base: 2, md: 4 }}>
              {filterUsers(allVendors).length === 0 ? (
                <Box
                  p={8}
                  textAlign="center"
                  bg="white"
                  borderRadius="lg"
                  shadow="md"
                >
                  <Text color="gray.500">
                    {searchQuery
                      ? "No vendors found matching your search"
                      : "No vendors registered yet"}
                  </Text>
                </Box>
              ) : isMobile ? (
                <>
                  <Box>
                    {getPaginatedData(
                      filterUsers(allVendors),
                      currentPage.allVendors
                    ).map((vendor) => (
                      <VendorCard
                        key={vendor._id}
                        vendor={vendor}
                        onEdit={handleEditUser}
                        onApprove={handleApproveVendor}
                        onReject={handleRejectVendor}
                        showStatus={true}
                      />
                    ))}
                  </Box>
                  <PaginationControls
                    data={filterUsers(allVendors)}
                    tabName="allVendors"
                  />
                </>
              ) : (
                <>
                  <Box
                    bg="white"
                    borderRadius="lg"
                    shadow="md"
                    overflowX="auto"
                  >
                    <Table variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Vendor</Th>
                          <Th>Company</Th>
                          <Th>Email</Th>
                          <Th>Phone</Th>
                          <Th>Categories</Th>
                          <Th>Location</Th>
                          <Th>Status</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {getPaginatedData(
                          filterUsers(allVendors),
                          currentPage.allVendors
                        ).map((vendor) => (
                          <Tr key={vendor._id}>
                            <Td>
                              <HStack>
                                <Avatar
                                  size="sm"
                                  name={vendor.name}
                                  src={vendor.pic}
                                />
                                <Text>{vendor.name}</Text>
                              </HStack>
                            </Td>
                            <Td>{vendor.companyName}</Td>
                            <Td>{vendor.email}</Td>
                            <Td>{vendor.phone}</Td>
                            <Td>
                              {vendor.categories?.slice(0, 2).join(", ")}
                              {vendor.categories?.length > 2 && "..."}
                            </Td>
                            <Td>{vendor.location}</Td>
                            <Td>
                              <VStack align="start" spacing={1}>
                                {vendor.isApproved === "approved" && (
                                  <Badge colorScheme="green">Approved</Badge>
                                )}
                                {vendor.isApproved === "pending" && (
                                  <Badge colorScheme="orange">Pending</Badge>
                                )}
                                {vendor.isApproved === "rejected" && (
                                  <Badge colorScheme="red">Rejected</Badge>
                                )}
                                {!vendor.isEmailVerified && (
                                  <Badge colorScheme="red">
                                    Unverified Email
                                  </Badge>
                                )}
                              </VStack>
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => handleEditUser(vendor)}
                                >
                                  Edit
                                </Button>
                                {vendor.isApproved === "pending" && (
                                  <>
                                    <Button
                                      size="sm"
                                      colorScheme="green"
                                      variant="outline"
                                      onClick={() =>
                                        handleApproveVendor(vendor._id)
                                      }
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      colorScheme="red"
                                      variant="outline"
                                      onClick={() =>
                                        handleRejectVendor(vendor._id)
                                      }
                                    >
                                      Reject
                                    </Button>
                                  </>
                                )}
                                {vendor.isApproved === "rejected" && (
                                  <Button
                                    size="sm"
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={() =>
                                      handleApproveVendor(vendor._id)
                                    }
                                  >
                                    Approve
                                  </Button>
                                )}
                                {vendor.isApproved === "approved" && (
                                  <Button
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={() =>
                                      handleRejectVendor(vendor._id)
                                    }
                                  >
                                    Reject
                                  </Button>
                                )}
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  <PaginationControls
                    data={filterUsers(allVendors)}
                    tabName="allVendors"
                  />
                </>
              )}
            </TabPanel>

            {/* Pending Vendors Tab */}
            <TabPanel p={{ base: 2, md: 4 }}>
              {filterUsers(pendingVendors).length === 0 ? (
                <Box
                  p={8}
                  textAlign="center"
                  bg="white"
                  borderRadius="lg"
                  shadow="md"
                >
                  <Text color="gray.500">
                    {searchQuery
                      ? "No pending vendors found matching your search"
                      : "No pending vendor applications"}
                  </Text>
                </Box>
              ) : isMobile ? (
                <>
                  <Box>
                    {getPaginatedData(
                      filterUsers(pendingVendors),
                      currentPage.pending
                    ).map((vendor) => (
                      <VendorCard
                        key={vendor._id}
                        vendor={vendor}
                        onEdit={handleEditUser}
                        onApprove={handleApproveVendor}
                        onReject={handleRejectVendor}
                      />
                    ))}
                  </Box>
                  <PaginationControls
                    data={filterUsers(pendingVendors)}
                    tabName="pending"
                  />
                </>
              ) : (
                <>
                  <Box
                    bg="white"
                    borderRadius="lg"
                    shadow="md"
                    overflowX="auto"
                  >
                    <Table variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Vendor</Th>
                          <Th>Company</Th>
                          <Th>Email</Th>
                          <Th>Phone</Th>
                          <Th>Categories</Th>
                          <Th>Location</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {getPaginatedData(
                          filterUsers(pendingVendors),
                          currentPage.pending
                        ).map((vendor) => (
                          <Tr key={vendor._id}>
                            <Td>
                              <HStack>
                                <Avatar
                                  size="sm"
                                  name={vendor.name}
                                  src={vendor.pic}
                                />
                                <Text>{vendor.name}</Text>
                              </HStack>
                            </Td>
                            <Td>{vendor.companyName}</Td>
                            <Td>{vendor.email}</Td>
                            <Td>{vendor.phone}</Td>
                            <Td>
                              {vendor.categories?.slice(0, 2).join(", ")}
                              {vendor.categories?.length > 2 && "..."}
                            </Td>
                            <Td>{vendor.location}</Td>
                            <Td>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  variant="outline"
                                  onClick={() => handleEditUser(vendor)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="green"
                                  onClick={() =>
                                    handleApproveVendor(vendor._id)
                                  }
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="red"
                                  variant="outline"
                                  onClick={() => handleRejectVendor(vendor._id)}
                                >
                                  Reject
                                </Button>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  <PaginationControls
                    data={filterUsers(pendingVendors)}
                    tabName="pending"
                  />
                </>
              )}
            </TabPanel>

            {/* Approved Vendors Tab */}
            <TabPanel p={{ base: 2, md: 4 }}>
              {filterUsers(approvedVendors).length === 0 ? (
                <Box
                  p={8}
                  textAlign="center"
                  bg="white"
                  borderRadius="lg"
                  shadow="md"
                >
                  <Text color="gray.500">
                    {searchQuery
                      ? "No approved vendors found matching your search"
                      : "No approved vendors yet"}
                  </Text>
                </Box>
              ) : isMobile ? (
                <>
                  <Box>
                    {getPaginatedData(
                      filterUsers(approvedVendors),
                      currentPage.approved
                    ).map((vendor) => (
                      <VendorCard
                        key={vendor._id}
                        vendor={vendor}
                        onEdit={handleEditUser}
                        onReject={handleRejectVendor}
                      />
                    ))}
                  </Box>
                  <PaginationControls
                    data={filterUsers(approvedVendors)}
                    tabName="approved"
                  />
                </>
              ) : (
                <>
                  <Box
                    bg="white"
                    borderRadius="lg"
                    shadow="md"
                    overflowX="auto"
                  >
                    <Table variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Vendor</Th>
                          <Th>Company</Th>
                          <Th>Email</Th>
                          <Th>Phone</Th>
                          <Th>Categories</Th>
                          <Th>Location</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {getPaginatedData(
                          filterUsers(approvedVendors),
                          currentPage.approved
                        ).map((vendor) => (
                          <Tr key={vendor._id}>
                            <Td>
                              <HStack>
                                <Avatar
                                  size="sm"
                                  name={vendor.name}
                                  src={vendor.pic}
                                />
                                <Text>{vendor.name}</Text>
                              </HStack>
                            </Td>
                            <Td>{vendor.companyName}</Td>
                            <Td>{vendor.email}</Td>
                            <Td>{vendor.phone}</Td>
                            <Td>
                              {vendor.categories?.slice(0, 2).join(", ")}
                              {vendor.categories?.length > 2 && "..."}
                            </Td>
                            <Td>{vendor.location}</Td>
                            <Td>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => handleEditUser(vendor)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="red"
                                  variant="outline"
                                  onClick={() => handleRejectVendor(vendor._id)}
                                >
                                  Reject
                                </Button>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  <PaginationControls
                    data={filterUsers(approvedVendors)}
                    tabName="approved"
                  />
                </>
              )}
            </TabPanel>

            {/* Rejected Vendors Tab */}
            <TabPanel p={{ base: 2, md: 4 }}>
              {filterUsers(rejectedVendors).length === 0 ? (
                <Box
                  p={8}
                  textAlign="center"
                  bg="white"
                  borderRadius="lg"
                  shadow="md"
                >
                  <Text color="gray.500">
                    {searchQuery
                      ? "No rejected vendors found matching your search"
                      : "No rejected vendors"}
                  </Text>
                </Box>
              ) : isMobile ? (
                <>
                  <Box>
                    {getPaginatedData(
                      filterUsers(rejectedVendors),
                      currentPage.rejected
                    ).map((vendor) => (
                      <VendorCard
                        key={vendor._id}
                        vendor={vendor}
                        onEdit={handleEditUser}
                        onApprove={handleApproveVendor}
                      />
                    ))}
                  </Box>
                  <PaginationControls
                    data={filterUsers(rejectedVendors)}
                    tabName="rejected"
                  />
                </>
              ) : (
                <>
                  <Box
                    bg="white"
                    borderRadius="lg"
                    shadow="md"
                    overflowX="auto"
                  >
                    <Table variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Vendor</Th>
                          <Th>Company</Th>
                          <Th>Email</Th>
                          <Th>Phone</Th>
                          <Th>Categories</Th>
                          <Th>Location</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {getPaginatedData(
                          filterUsers(rejectedVendors),
                          currentPage.rejected
                        ).map((vendor) => (
                          <Tr key={vendor._id}>
                            <Td>
                              <HStack>
                                <Avatar
                                  size="sm"
                                  src={vendor.pic}
                                  name={vendor.name}
                                />
                                <Text fontWeight="medium">{vendor.name}</Text>
                              </HStack>
                            </Td>
                            <Td>{vendor.companyName}</Td>
                            <Td>{vendor.email}</Td>
                            <Td>{vendor.phone}</Td>
                            <Td>
                              {vendor.categories?.slice(0, 2).join(", ")}
                              {vendor.categories?.length > 2 && "..."}
                            </Td>
                            <Td>{vendor.location}</Td>
                            <Td>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => handleEditUser(vendor)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="green"
                                  variant="outline"
                                  onClick={() =>
                                    handleApproveVendor(vendor._id)
                                  }
                                >
                                  Approve
                                </Button>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  <PaginationControls
                    data={filterUsers(rejectedVendors)}
                    tabName="rejected"
                  />
                </>
              )}
            </TabPanel>

            {/* Customers Tab */}
            <TabPanel p={{ base: 2, md: 4 }}>
              {filterUsers(allCustomers).length === 0 ? (
                <Box
                  p={8}
                  textAlign="center"
                  bg="white"
                  borderRadius="lg"
                  shadow="md"
                >
                  <Text color="gray.500">
                    {searchQuery
                      ? "No customers found matching your search"
                      : "No customers registered yet"}
                  </Text>
                </Box>
              ) : isMobile ? (
                <>
                  <Box>
                    {getPaginatedData(
                      filterUsers(allCustomers),
                      currentPage.customers
                    ).map((customer) => (
                      <CustomerCard
                        key={customer._id}
                        customer={customer}
                        onEdit={handleEditUser}
                        onBan={handleBanCustomer}
                        onUnban={handleUnbanCustomer}
                      />
                    ))}
                  </Box>
                  <PaginationControls
                    data={filterUsers(allCustomers)}
                    tabName="customers"
                  />
                </>
              ) : (
                <>
                  <Box
                    bg="white"
                    borderRadius="lg"
                    shadow="md"
                    overflowX="auto"
                  >
                    <Table variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Customer</Th>
                          <Th>Email</Th>
                          <Th>Status</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {getPaginatedData(
                          filterUsers(allCustomers),
                          currentPage.customers
                        ).map((customer) => (
                          <Tr key={customer._id}>
                            <Td>
                              <HStack>
                                <Avatar
                                  size="sm"
                                  name={customer.name}
                                  src={customer.pic}
                                />
                                <Text>{customer.name}</Text>
                              </HStack>
                            </Td>
                            <Td>{customer.email}</Td>
                            <Td>
                              <VStack align="start" spacing={1}>
                                <Badge
                                  colorScheme={
                                    customer.isBanned ? "red" : "green"
                                  }
                                >
                                  {customer.isBanned ? "Banned" : "Active"}
                                </Badge>
                                {!customer.isEmailVerified && (
                                  <Badge colorScheme="red">
                                    Unverified Email
                                  </Badge>
                                )}
                              </VStack>
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => handleEditUser(customer)}
                                >
                                  Edit
                                </Button>
                                {customer.isBanned ? (
                                  <Button
                                    size="sm"
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={() =>
                                      handleUnbanCustomer(customer._id)
                                    }
                                  >
                                    Unban
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={() =>
                                      handleBanCustomer(customer._id)
                                    }
                                  >
                                    Ban
                                  </Button>
                                )}
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  <PaginationControls
                    data={filterUsers(allCustomers)}
                    tabName="customers"
                  />
                </>
              )}
            </TabPanel>

            {/* Low Rated Vendors Tab */}
            <TabPanel p={{ base: 2, md: 4 }}>
              {lowRatedVendors.length === 0 ? (
                <Box
                  p={8}
                  textAlign="center"
                  bg="white"
                  borderRadius="lg"
                  shadow="md"
                >
                  <Text color="gray.500">
                    No vendors with ratings of 2 stars or below
                  </Text>
                </Box>
              ) : isMobile ? (
                <>
                  <Box>
                    {getPaginatedData(
                      lowRatedVendors,
                      currentPage.lowRated
                    ).map((vendor) => (
                      <VendorCard
                        key={vendor._id}
                        vendor={vendor}
                        onEdit={handleEditUser}
                        showStatus={true}
                      />
                    ))}
                  </Box>
                  <PaginationControls
                    data={lowRatedVendors}
                    tabName="lowRated"
                  />
                </>
              ) : (
                <>
                  <Box
                    overflowX="auto"
                    bg="white"
                    borderRadius="lg"
                    shadow="md"
                  >
                    <Table variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Name</Th>
                          <Th>Company</Th>
                          <Th>Rating</Th>
                          <Th>Total Ratings</Th>
                          <Th>Email</Th>
                          <Th>Phone</Th>
                          <Th>Categories</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {getPaginatedData(
                          lowRatedVendors,
                          currentPage.lowRated
                        ).map((vendor) => (
                          <Tr key={vendor._id}>
                            <Td>
                              <HStack>
                                <Avatar
                                  size="sm"
                                  name={vendor.name}
                                  src={vendor.pic}
                                />
                                <Text>{vendor.name}</Text>
                              </HStack>
                            </Td>
                            <Td>{vendor.companyName}</Td>
                            <Td>
                              <HStack>
                                <Text
                                  fontWeight="bold"
                                  color={
                                    vendor.averageRating <= 1
                                      ? "red.500"
                                      : "orange.500"
                                  }
                                >
                                  ⭐ {vendor.averageRating?.toFixed(1) || "0.0"}
                                </Text>
                              </HStack>
                            </Td>
                            <Td>
                              <Badge colorScheme="blue">
                                {vendor.totalRatings || 0} reviews
                              </Badge>
                            </Td>
                            <Td>{vendor.email}</Td>
                            <Td>{vendor.phone}</Td>
                            <Td>
                              {vendor.categories?.slice(0, 2).join(", ")}
                              {vendor.categories?.length > 2 && "..."}
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => handleEditUser(vendor)}
                                >
                                  View
                                </Button>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  <PaginationControls
                    data={lowRatedVendors}
                    tabName="lowRated"
                  />
                </>
              )}
            </TabPanel>

            {/* Categories Tab */}
            <TabPanel p={{ base: 2, md: 4 }}>
              <VStack spacing={4} align="stretch">
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size={{ base: "sm", md: "md" }}>
                    Manage Categories
                  </Heading>
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="blue"
                    size={{ base: "sm", md: "md" }}
                    onClick={() => handleOpenCategoryModal()}
                  >
                    New Category
                  </Button>
                </Flex>

                {categories.length === 0 ? (
                  <Box
                    p={8}
                    textAlign="center"
                    bg="white"
                    borderRadius="lg"
                    shadow="md"
                  >
                    <Text color="gray.500">
                      No categories created yet. Click "New Category" to add
                      one.
                    </Text>
                  </Box>
                ) : (
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {categories.map((category) => (
                      <Box
                        key={category._id}
                        bg="white"
                        p={4}
                        borderRadius="lg"
                        shadow="md"
                        borderWidth="1px"
                        borderColor="gray.200"
                        _hover={{ shadow: "lg", borderColor: "blue.300" }}
                        transition="all 0.2s"
                      >
                        <VStack align="stretch" spacing={3}>
                          <Flex justify="space-between" align="start">
                            <HStack spacing={3}>
                              {category.icon && (
                                <Box
                                  p={2}
                                  bg="blue.50"
                                  borderRadius="md"
                                  color="blue.600"
                                >
                                  <DynamicIcon
                                    iconName={category.icon}
                                    size={24}
                                  />
                                </Box>
                              )}
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="bold" fontSize="lg">
                                  {category.name}
                                </Text>
                                {category.description && (
                                  <Text fontSize="sm" color="gray.600">
                                    {category.description}
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                            <Badge
                              colorScheme={category.isActive ? "green" : "gray"}
                            >
                              {category.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </Flex>

                          <Divider />

                          <HStack spacing={2} justify="flex-end">
                            <Button
                              size="sm"
                              leftIcon={<EditIcon />}
                              colorScheme="blue"
                              variant="outline"
                              onClick={() => handleOpenCategoryModal(category)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              leftIcon={<DeleteIcon />}
                              colorScheme="red"
                              variant="outline"
                              onClick={() => handleDeleteCategory(category._id)}
                            >
                              Delete
                            </Button>
                          </HStack>
                        </VStack>
                      </Box>
                    ))}
                  </SimpleGrid>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Category Modal */}
      <Modal
        isOpen={isCategoryModalOpen}
        onClose={onCategoryModalClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingCategory ? "Edit Category" : "Create New Category"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Category Name</FormLabel>
                <Input
                  placeholder="e.g., Plumbing, Electrical, HVAC"
                  value={newCategory.name}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, name: e.target.value })
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  placeholder="Brief description of the category"
                  value={newCategory.description}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      description: e.target.value,
                    })
                  }
                  rows={3}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Icon</FormLabel>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Select an icon for this category:
                </Text>
                <Grid
                  templateColumns="repeat(auto-fill, minmax(80px, 1fr))"
                  gap={2}
                  maxH="300px"
                  overflowY="auto"
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                >
                  {Object.entries(categoryIconSuggestions).map(
                    ([categoryName, iconName]) => (
                      <Box
                        key={iconName}
                        p={3}
                        borderWidth="2px"
                        borderRadius="md"
                        borderColor={
                          newCategory.icon === iconName
                            ? "blue.500"
                            : "gray.200"
                        }
                        bg={newCategory.icon === iconName ? "blue.50" : "white"}
                        cursor="pointer"
                        textAlign="center"
                        transition="all 0.2s"
                        _hover={{
                          borderColor: "blue.400",
                          bg: "blue.50",
                          transform: "scale(1.05)",
                        }}
                        onClick={() =>
                          setNewCategory({ ...newCategory, icon: iconName })
                        }
                      >
                        <VStack spacing={1}>
                          <DynamicIcon
                            iconName={iconName}
                            size={24}
                            color={
                              newCategory.icon === iconName
                                ? "#3182ce"
                                : "#718096"
                            }
                          />
                          <Text fontSize="xs" color="gray.600" noOfLines={1}>
                            {categoryName}
                          </Text>
                        </VStack>
                      </Box>
                    )
                  )}
                </Grid>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onCategoryModalClose}
              isDisabled={categoryLoading}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveCategory}
              isLoading={categoryLoading}
            >
              {editingCategory ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={editUser}
        onSuccess={handleEditSuccess}
        categories={categories}
      />

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleEditSuccess}
        categories={categories}
      />
    </Container>
  );
};

export default AdminDashboard;
